// Clinician service for API calls
import apiClient from '../../../services/apiClient';

const clinicianService = {
  // Get all clinicians for admin interface
  getAllClinicians: async () => {
    try {
      console.log('🔑 Fetching all clinicians via apiClient');
      const response = await apiClient.get('/clinicians/');
      console.log('✅ Clinicians data received:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching clinicians:', error);
      throw error;
    }
  },

  // Get clinician by ID
  getClinicianById: async (clinicianId) => {
    try {
      const response = await apiClient.get(`/clinicians/${clinicianId}`);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching clinician by ID:', error);
      throw error;
    }
  },

  // Create new clinician
  createClinician: async (clinicianData) => {
    try {
      const response = await apiClient.post('/clinicians/', clinicianData);
      return response.data;
    } catch (error) {
      console.error('❌ Error creating clinician:', error);
      throw error;
    }
  },

  // Create new clinician with user account (for admin use)
  createClinicianWithUser: async (clinicianData) => {
    try {
      const response = await apiClient.post('/clinicians/create-with-user', clinicianData);
      return response.data;
    } catch (error) {
      console.error('❌ Error creating clinician with user:', error);
      // Preserve original error message from server
      if (error.response?.data) {
        throw new Error(typeof error.response.data === 'string' ? error.response.data : JSON.stringify(error.response.data));
      }
      throw error;
    }
  },

  // Update clinician
  updateClinician: async (clinicianId, clinicianData) => {
    try {
      const response = await apiClient.put(`/clinicians/${clinicianId}`, clinicianData);
      return response.data;
    } catch (error) {
      console.error('❌ Error updating clinician:', error);
      throw error;
    }
  },

  // Update AC vitals access for clinician
  updateAcVitalsAccess: async (clinicianId, acVitalsAccess, accessDays) => {
    try {
      const params = {
        ac_vitals_access: acVitalsAccess
      };
      if (accessDays !== null && accessDays !== undefined) {
        params.access_days = accessDays;
      }
      
      const response = await apiClient.put(`/clinicians/${clinicianId}/ac-vitals-access`, null, { params });
      return response.data;
    } catch (error) {
      console.error('❌ Error updating AC vitals access:', error);
      throw error;
    }
  },

  // Check AC vitals access for clinician
  checkAcVitalsAccess: async (clinicianId) => {
    try {
      const response = await apiClient.get(`/clinicians/${clinicianId}/ac-vitals-access`);
      return response.data;
    } catch (error) {
      console.error('❌ Error checking AC vitals access:', error);
      throw error;
    }
  },

  // Delete clinician
  deleteClinician: async (clinicianId) => {
    try {
      const response = await apiClient.delete(`/clinicians/${clinicianId}`);
      return response.data;
    } catch (error) {
      console.error('❌ Error deleting clinician:', error);
      throw error;
    }
  },

  // Get specialties
  getSpecialties: async () => {
    try {
      const response = await apiClient.get('/clinicians/specialties');
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching clinician specialties:', error);
      throw error;
    }
  },

  // Search clinicians
  searchClinicians: async (searchTerm) => {
    try {
      const response = await apiClient.get(`/clinicians/search/${encodeURIComponent(searchTerm)}`);
      return response.data;
    } catch (error) {
      console.error('❌ Error searching clinicians:', error);
      throw error;
    }
  },

  // Get available clinics for assignment
  getAvailableClinics: async () => {
    try {
      console.log('🏥 Frontend - Fetching available clinics via apiClient');
      const response = await apiClient.get('/clinicians/available-clinics');
      console.log('🏥 Frontend - Successfully received clinics data:', response.data);
      return response.data.available_clinics || [];
    } catch (error) {
      console.error('🏥 Frontend - Error fetching available clinics:', error);
      throw error;
    }
  },

  // Assign clinic to clinician
  assignClinicToClinician: async (clinicianId, clinicId) => {
    try {
      const response = await apiClient.post(`/clinicians/${clinicianId}/assign-clinic`, {
        clinic_id: clinicId,
        is_primary_clinic: true
      });
      return response.data;
    } catch (error) {
      console.error('❌ Error assigning clinic to clinician:', error);
      throw error;
    }
  },

  // Remove clinic from clinician
  removeClinicFromClinician: async (clinicianId, clinicId = null) => {
    try {
      const url = clinicId 
        ? `/clinicians/${clinicianId}/remove-clinic?clinic_id=${clinicId}`
        : `/clinicians/${clinicianId}/remove-clinic`;
        
      const response = await apiClient.delete(url);
      return response.data;
    } catch (error) {
      console.error('❌ Error removing clinic from clinician:', error);
      throw error;
    }
  },
};

export { clinicianService };
export default clinicianService;
